import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldAlert } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Kicker } from "@/components/Reveal";
import { FinalCTA } from "@/pages/Home";
import { api } from "@/lib/api";

export default function Brands() {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/products/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const brands = [
    {
      name: "Multispan",
      desc: "Automation and control products — temperature controllers, PID controllers, timers, protection relays, SMPS and switches.",
      type: "PID Controller",
    },
    {
      name: "Crompton",
      desc: "Professional & domestic lighting, fans and house wires — LED panels, downlights, Laser Ray battens, Param lamps, street & flood lights, SilentPro/Energion/Highspeed ceiling fans, exhaust & industrial fans, and Armor house wires.",
      category: "lighting",
    },
    {
      name: "DeWalt",
      desc: "Industrial power tools for workshops, contractors and professional applications.",
      category: "tools",
    },
    {
      name: "L&T (Lauritz Knudsen)",
      desc: "Switchgear products — contactors, overload relays, MCCBs, MPCBs and accessories. Branding shown as currently listed by the business.",
      category: "switchgear",
    },
    {
      name: "Siemens",
      desc: "SIRIUS low-voltage control products — 3RT2 power contactors, 3RH2 contactor relays, 3RU2/3RB overload relays, 3RV2 MPCBs, 3RW soft starters, 3MT7 capacitor duty contactors, 3SU1/3SB5 push buttons, 3SE5 position switches, 3SK1 safety relays, 4RB2 capacitors and more.",
      category: "switchgear",
    },
    {
      name: "Schneider",
      desc: "Panel builder range — EasyPact/ComPact MCCBs, TeSys contactors & overload relays, MPCBs, Harmony relays, timers & push buttons, Phaseo power supplies, Altivar drives & soft starters, Acti 9 MCBs/RCDs, Vigirex earth leakage relays.",
      category: "switchgear",
    },
    {
      name: "SELEC",
      desc: "Process automation and electrical products — MiBRX / Flexys / fixed-I/O PLCs, HMI, VFD drives, power supplies, PID & temperature controllers, timers, counters, panel meters, APFC controllers, protection relays, power quality and solar inverters.",
      type: "PLC",
    },
    {
      name: "Polycab",
      desc: "House wires and cables — Green Wire+, Optima+, Primma and Etira ranges in 90m and 180m rolls. List less 44% on 90m rolls and less 50% on 180m rolls.",
      category: "electrical",
    },
    {
      name: "ABB",
      desc: "Electrification low-voltage products — AX/AFC contactors, TA/EF overload relays, MS manual motor starters, SB200M/S200 MCBs, SACE Tmax XT & Formula MCCBs, CT time relays, CM monitoring relays and CR interface relays.",
      category: "switchgear",
    },
    {
      name: "FTC",
      desc: "Panel switchgear & accessories — changeover & load break switches, switch fuse disconnectors, HRC fuses, busbar chambers, terminal blocks, control & signalling devices, distribution boards, plugs & sockets, panel meters and panel accessories.",
      category: "switchgear",
    },
    {
      name: "Janatics",
      desc: "Pneumatic products — directional control valves (J/DP1/DS2/DS3/DS4/DS5 series), NAMUR & ISO valves, manual/mechanical/foot valves, air line valves (flow control, quick exhaust, non-return, logic), air preparation units (F1 filters, R1 regulators, L1 lubricators, FRLM modular FRLs, precision & proportional regulators, air tanks), plus one-touch fittings, PU tubes, blow guns, quick couplers, silencers and pneumatic panels.",
      category: "pneumatic",
    },
  ];

  return (
    <>
      <SEO
        title="Brands | Hari Om Trading Co. — Tahliwal, Una"
        description="Brands supplied by Hari Om Trading Co., Tahliwal: Multispan automation products, Crompton lighting, DeWalt industrial tools and more."
      />
      <section className="grain bg-[#111111] bg-grid-dark py-16 lg:py-24" data-testid="brands-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <Kicker dark>Brands</Kicker>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Brands We Supply
            </h1>
            <p className="mt-4 max-w-2xl text-base text-[#b8bec8]">
              Product brands currently listed in our catalogue. Brand availability may change —
              send an enquiry to confirm a specific model.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F6F7F8] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-5 md:grid-cols-3">
            {brands.map((b, i) => {
              const count = meta?.brand_counts?.[b.name] || 0;
              const link = b.type ? `/products?ptype=${encodeURIComponent(b.type)}` : `/products?category=${b.category}`;
              return (
                <Reveal key={b.name} delay={i * 0.08}>
                  <Link
                    to={b.type ? `/products?brand=${encodeURIComponent(b.name)}` : link}
                    data-testid={`brand-card-${b.name.toLowerCase()}`}
                    className="group flex h-full flex-col border border-[#D9DDE3] bg-white p-8 transition-colors hover:border-[#111111]"
                  >
                    <p className="font-heading text-xs font-bold uppercase tracking-[0.22em] text-[#F5B700]">
                      {String(i + 1).padStart(2, "0")}{count > 0 ? ` · ${count} catalogue entries` : ""}
                    </p>
                    <h2 className="mt-4 font-heading text-3xl font-bold text-[#161616]">{b.name}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#667085]">{b.desc}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#161616] group-hover:text-[#8a6800]">
                      View products <ArrowUpRight size={15} aria-hidden="true" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-10 flex items-start gap-3 border border-[#D9DDE3] bg-white p-6" data-testid="brands-disclaimer">
              <ShieldAlert size={20} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
              <p className="text-sm leading-relaxed text-[#667085]">
                <strong className="text-[#161616]">[ADD AUTHORIZED BRAND STATUS]</strong> — Authorized-dealer
                claims are shown only after confirmation from the business. Additional supplied brands can be
                added here by the site administrator.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
